import { type ReactNode, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Button from '@cx-web/components/primitives/button';
import { type SubsProduct } from '@cx-web/domain-types';
import { useRepubContext } from '@cx-web/feature-repub';
import { Card } from '@fpsm/cxy-components/Card';
import { IconCheck, IconCross } from '@fpsm/cxy-icons';
import clsx from 'clsx';

import ButtonLoader from '~/apps/product-plus/components/Loaders/ButtonLoader';

import { type Selection } from '../hooks/use-subscribe';

import styles from './SubscriptionCard.module.css';

export interface Feature {
  description?: string;
  isDisabled?: boolean;
  title: string;
}

export type SubscriptionCardProps = {
  featured?: boolean;
  features: Feature[] | string[];
  handleSubscribe: (selection: Selection) => void;
  href?: string;
  isEssentialSubscriber?: boolean;
  isLoggedIn: boolean;
  pricingLabel: string;
  product: SubsProduct;
  title: string;
  userHasCancelledSubscription?: boolean;
  variant: 'premium' | 'essential';
};

const FeatureItem = ({ description, isDisabled, title }: Feature) => {
  const Icon = isDisabled ? IconCross : IconCheck;

  return (
    <li className={clsx(styles.featureItem, { [styles.disabled]: isDisabled })}>
      <span className={styles.icon}>
        <Icon size="s" />
      </span>
      <span>
        <strong>
          {title}
          {!!description && ':'}
        </strong>
        {description && ` ${description}`}
      </span>
    </li>
  );
};

export const SubscriptionCard = ({
  featured,
  features,
  handleSubscribe,
  href,
  isEssentialSubscriber,
  isLoggedIn,
  pricingLabel,
  product,
  title,
  userHasCancelledSubscription,
  variant,
}: SubscriptionCardProps) => {
  const { discount, discountedPrice, priceFormatted, term } = product;
  const [isLoading, setIsLoading] = useState(false);
  const { repubIs } = useRepubContext();
  const { t } = useTranslation(['label']);

  const onClick = () => {
    setIsLoading(true);

    handleSubscribe({
      productId: product.productId,
    });
  };

  const getButtonText = () => {
    if (isEssentialSubscriber) {
      return 'Upgrade to Premium';
    }
    if (variant === 'essential' || userHasCancelledSubscription || repubIs.WS) {
      return `Purchase ${variant}`;
    }
    return t('CX_PLUS_START_7_DAY_TRIAL');
  };

  const displayPrice = discountedPrice || priceFormatted;
  const [wholePart, decimalPart] = displayPrice.includes('.') ? displayPrice.split('.') : [displayPrice, '00'];

  const discountString = discount || '';
  const [discountValue, discountTerms] = discountString.includes(' | ')
    ? discountString.split(' | ')
    : [discountString, ''];
  const hasDiscount = !!discountValue;

  const defaultDiscountTerms = hasDiscount
    ? `Paid ${term}. Valid for 12 months. ${priceFormatted} thereafter.`
    : `Paid ${term}`;

  return (
    <div className={styles.cardWrapper}>
      <Card
        className={clsx(styles.card, { [styles.featured]: featured })}
        data-testid="subscription-card-v2"
        variant="secondary"
      >
        <div className={styles.header}>
          <h3 className={styles.title}>{title}</h3>
          <div
            className={clsx(styles.pricing, {
              [styles.pricingWithSave]: hasDiscount,
            })}
          >
            <p className={styles.pricingLabel}>{pricingLabel}</p>
            <div>
              {hasDiscount && (
                <div className={styles.saveTag} data-testid="tag">
                  {discountValue}
                </div>
              )}
              <div className={styles.price}>
                <span className={styles.currentPrice} data-testid="price">
                  {wholePart}
                  <span className={styles.decimal}>{decimalPart ? `.${decimalPart}` : ''}</span>
                </span>
                {(discountedPrice || discountedPrice) && (
                  <span className={styles.originalPrice} data-testid="original-price">
                    was {priceFormatted}
                  </span>
                )}
              </div>
              <p className={styles.paidTerm} data-testid="term">
                {discountTerms || defaultDiscountTerms}
              </p>
            </div>
          </div>
        </div>

        <div className={styles.features}>
          <ul className={styles.featureList}>
            {features.map((feature, index) =>
              typeof feature === 'string' ? (
                <FeatureItem isDisabled={false} key={index} title={feature} />
              ) : (
                <FeatureItem key={index} {...feature} />
              ),
            )}
          </ul>
        </div>
        {href ? (
          <a className={styles.purchaseLink} href={href}>
            {getButtonText()}
          </a>
        ) : (
          <Button
            className={styles.purchaseButton}
            disabled={isLoggedIn && isLoading}
            onClick={onClick}
            type="secondary"
          >
            {isLoggedIn && isLoading ? (
              <>
                <span className="sr-only">{getButtonText()}</span>
                <ButtonLoader />
              </>
            ) : (
              getButtonText()
            )}
          </Button>
        )}
      </Card>
    </div>
  );
};

interface ContainerProps {
  children: ReactNode;
}

const Container = ({ children }: ContainerProps) => {
  return (
    <div className={styles.containerWrapper}>
      <div className={styles.container}>{children}</div>
    </div>
  );
};

SubscriptionCard.Container = Container;
